/**
 * The api's MFA gate (ENG-6095, ENG-6103).
 *
 * A Cognito user who had MFA before the migration and holds no Cognito factor
 * yet gets HTTP 403 `auth.mfa_setup_required` on every GraphQL operation except
 * the viewer query and `otpConfiguration`, and the websocket closes with 1008
 * and the same reason. Neither is a logout: the user is sent to /mfa_setup,
 * enrols TOTP through Amplify, and comes back to where they were.
 *
 * Leaf module on purpose: src/core/api.ts and apiWebSocket.ts import it.
 */
import ERROR_CODES from '@/core/apiErrorCodes';

import { isCognitoSessionActive } from './cognitoSession';

export const MFA_SETUP_PATH = '/mfa_setup';
const POLICY_VIOLATION = 1008;

/** True for the gate's 403 as an Apollo link sees it (`ServerError`). */
export function isMfaGateResponse(networkError: unknown): boolean {
  if (!networkError || typeof networkError !== 'object' || !('result' in networkError)) {
    return false;
  }
  const { statusCode, result } = networkError as {
    statusCode?: number;
    result?: { code?: string };
  };
  return statusCode === 403 && result?.code === ERROR_CODES.AUTH_MFA_SETUP_REQUIRED;
}

/** True for the websocket close the gate sends. */
export function isMfaGateClose(evt: { code: number; reason: string }): boolean {
  return evt.code === POLICY_VIOLATION && evt.reason === ERROR_CODES.AUTH_MFA_SETUP_REQUIRED;
}

/**
 * A same-origin path to resume at, else `fallback`. Parsed rather than
 * pattern-matched: browsers strip tabs and newlines and read a backslash as a
 * slash, so "/\t/evil.com" or "/\\evil.com" would otherwise leave the origin.
 */
export function safeNextPath(
  next: string | null | undefined,
  fallback?: string
): string | undefined {
  if (!next || !next.startsWith('/')) return fallback;
  try {
    const url = new URL(next, window.location.origin);
    if (url.origin !== window.location.origin) return fallback;
    // Dot segments ("/.//evil", "/%2e//evil") normalise to a path that a
    // later navigation would read as protocol-relative.
    if (url.pathname.startsWith('//') || url.pathname.startsWith('/\\')) return fallback;
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}

/**
 * Send the user to enrolment, remembering where they were. Only for a Cognito
 * session, and never from the pages that run sign-in or enrolment themselves.
 * True when it started the navigation.
 */
export function routeToMfaSetup(): boolean {
  if (!isCognitoSessionActive() || typeof window === 'undefined') return false;
  const { pathname, search } = window.location;
  if (['/login', '/logout', MFA_SETUP_PATH].some((path) => pathname.startsWith(path))) return false;
  window.location.assign(`${MFA_SETUP_PATH}?next=${encodeURIComponent(pathname + search)}`);
  return true;
}
